import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronUp, PenLine } from "lucide-react";
import { useEffect, useState } from "react";
import { jobProgress, jobStatusLine } from "../lib/job-progress";
import { BrandSymbol } from "./BrandMark";
import type { GenerationJob } from "../types";

const VISIBLE_JOBS = 3;

/**
 * Пульт идущих выпусков. Полноэкранный оверлей интересно посмотреть раз, но
 * держать его открытым всю генерацию незачем: рукопись пишется в фоне, а
 * читателю нужно только видеть, что она пишется, и уметь вернуться к подробностям.
 *
 * Заданий может идти несколько (`MAX_CONCURRENT_JOBS`), поэтому пульт — список,
 * а не одна плашка. На узких экранах он ужимается до одной строки со счётчиком
 * остальных и прячется, пока читатель листает вниз: под текстом истории он
 * мешает больше, чем помогает.
 */
export function GenerationDock({ jobs, onOpen }: { jobs: GenerationJob[]; onOpen: (job: GenerationJob) => void }) {
  const reduceMotion = useReducedMotion();
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const current = window.scrollY;
      if (Math.abs(current - last) < 26) return;
      setHidden(current > last && current > 240);
      last = current;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const visible = jobs.slice(0, VISIBLE_JOBS);
  // Прятать пульт классом нельзя: motion держит `transform` в инлайновом стиле и
  // перебивает любой CSS. Поэтому уход вниз — то же свойство анимации, что и появление.
  return <AnimatePresence>{jobs.length > 0 && <motion.aside
    className="generation-dock"
    aria-label={jobs.length > 1 ? `Идёт выпусков: ${jobs.length}` : "Идёт выпуск"}
    initial={reduceMotion ? false : { opacity: 0, y: 24 }}
    animate={{ opacity: hidden ? 0 : 1, y: hidden && !reduceMotion ? 120 : 0 }}
    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 18 }}
    transition={{ duration: reduceMotion ? 0 : .26, ease: [0.22, 1, 0.36, 1] }}
    style={{ pointerEvents: hidden ? "none" : "auto" }}
    aria-hidden={hidden}
  >
    {visible.map((job, index) => {
      const percent = Math.round(jobProgress(job));
      return <button
        key={job.id}
        type="button"
        className={index ? "dock-job dock-extra" : "dock-job"}
        onClick={() => onOpen(job)}
        aria-label={`Открыть ход выпуска «${job.topic}», готовность ${percent}%`}
      >
        <span className="dock-mark" aria-hidden="true"><BrandSymbol /></span>
        <span className="dock-copy">
          <b>{job.topic}</b>
          <small>{jobStatusLine(job)}</small>
          <span className="dock-meter" aria-hidden="true"><i style={{ width: `${percent}%` }} /></span>
        </span>
        <span className="dock-percent">{percent}%</span>
        {index === 0 && jobs.length > 1 && <em className="dock-count" aria-hidden="true">+{jobs.length - 1}</em>}
        <ChevronUp className="dock-open" size={15} aria-hidden="true" />
      </button>;
    })}
    {jobs.length > VISIBLE_JOBS && <span className="dock-more">и ещё {jobs.length - VISIBLE_JOBS} в работе</span>}
  </motion.aside>}</AnimatePresence>;
}
